import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { FooterUnderageParagraph, FooterUnderageRichTextLink, FooterUnderageSectionConfig } from '../../../../config/footer.types.ts';
import { Link } from '../../Link/Link';
import { ThemeAwareImage } from '../components/ThemeAwareImage';
import { AGE_SAFE_IMAGES } from './underageSectionImages';

type UnderageSectionProps = {
  config: FooterUnderageSectionConfig;
};

function buildRichTextMapping(links: FooterUnderageRichTextLink[]) {
  return Object.fromEntries(
    links.map(link => [
      link.placeholder,
      (chunks: ReactNode) => (
        <Link href={link.href} data-track-name={link.trackName} className="link-label-s">
          {chunks}
        </Link>
      ),
    ])
  );
}

function renderParagraph(paragraph: FooterUnderageParagraph, t: ReturnType<typeof useTranslations>) {
  if (paragraph.richTextLinks?.length) {
    return t.rich(paragraph.translationKey, buildRichTextMapping(paragraph.richTextLinks));
  }

  return t(paragraph.translationKey);
}

function UnderageSection({ config }: UnderageSectionProps) {
  const t = useTranslations('footer.underage');
  const { paragraphs, imageSection } = config;

  return (
    <div data-track-container="Legal">
      {paragraphs.map((paragraph, index) => (
        <p className={index < paragraphs.length - 1 ? 'paragraph-s mb-4' : 'paragraph-s'} key={paragraph.translationKey}>
          {renderParagraph(paragraph, t)}
        </p>
      ))}

      {imageSection ? (
        <div className="flex flex-col gap-2 mt-4 xl:flex-row xl:justify-between">
          <div className="flex items-center">
            <p className="paragraph-s-strong mr-3">{t(imageSection.leadImageTextKey)}</p>

            <ThemeAwareImage
              src={AGE_SAFE_IMAGES[imageSection.leadImage.imageKey]}
              alt={imageSection.leadImage.alt}
              width={imageSection.leadImage.width}
              height={imageSection.leadImage.height}
              className="w-8 h-8"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {imageSection.items?.map(item => {
              if (item.href) {
                return (
                  <Link key={item.imageKey} href={item.href} data-track-name={item.trackName}>
                    <ThemeAwareImage src={AGE_SAFE_IMAGES[item.imageKey]} alt={item.alt} width={item.width} height={item.height} className="w-20 h-20" />
                  </Link>
                );
              }

              return <ThemeAwareImage key={item.imageKey} src={AGE_SAFE_IMAGES[item.imageKey]} alt={item.alt} width={item.width} height={item.height} className="w-20 h-20" />;
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { AGE_SAFE_IMAGES, UnderageSection };
